import { motion, useReducedMotion } from "motion/react"
import { type FC, useState } from "react"
import { Navigate, useNavigate } from "react-router"

import { type Plan as PlanType } from "@forento/core/models/payment"
import { getPlansAnnualDiscount } from "@forento/core/utilities/payment"

import Button from "~/components/Button"
import { ArrowIcon } from "~/components/Icon"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import PaymentIntervalSelector from "~/components/PaymentIntervalSelector"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import Layout, { Logo } from "../Layout"
import Plan from "./Plan"

const SelectPlanPage: FC = () => {
	const platform = usePlatform()!
	const navigate = useNavigate()
	const prefersReducedMotion = useReducedMotion()

	const [isMonthlySelected, setMonthlySelected] = useState(false)
	const [submittingPlanId, setSubmittingPlanId] = useState<number>()

	const plans = query.payment.creator.listPlans.useQuery()

	const nextStepUrl = routes.account.academyCreated()

	if (
		platform.plan.type === "appsumo" ||
		platform.plan.type === "viededingue" ||
		platform.plan.type === "productcanyon"
	) {
		return <Navigate to={nextStepUrl} />
	}

	async function handleSelectPlan(plan: PlanType) {
		if (plan.id === null) throw new Error("Chosen plan's ID is null!")

		setSubmittingPlanId(plan.id)
		try {
			const response = await trpc.payment.creator.setPlan.mutate({
				planId: plan.id,
				paymentInterval: isMonthlySelected ? "month" : "year",
				redirect: {
					cancel: routes.account.selectPlan(),
					success: nextStepUrl,
				},
				source: "onboarding",
			})
			if (response.redirectUrl === null) {
				return navigate(nextStepUrl)
			}

			window.location.assign(response.redirectUrl)
		} catch (error) {
			console.error(error)
			setSubmittingPlanId(undefined)
		}
	}

	return (
		<Layout>
			<main className="flex w-full max-w-336 flex-col items-center py-8 sm:py-12">
				<Logo className="mb-8" />
				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="flex max-w-xl flex-col items-center text-center"
				>
					<p className="mb-3 rounded-full bg-[#ecfccb] px-3 py-1 text-xs font-bold tracking-[0.16em] text-[#4d7c0f] uppercase">
						Almost there
					</p>
					<h1 className="text-3xl leading-tight font-bold tracking-tight text-slate-950 sm:text-4xl">
						Choose your plan
					</h1>
					<p className="mt-3 text-base leading-relaxed font-medium text-slate-500">
						Every paid plan starts with a 30-day free trial. Cancel anytime.
					</p>
				</motion.div>
				{plans.error ? (
					<p className="mt-9 text-slate-600">Failed to load plans.</p>
				) : plans.data === undefined ? (
					<PartialLoadingPage />
				) : (
					<motion.div
						initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.15, duration: 0.35 }}
						className="mt-9 flex w-full flex-col items-center"
					>
						<PaymentIntervalSelector
							annualDiscount={getPlansAnnualDiscount(plans.data)}
							isMonthlySelected={isMonthlySelected}
							setMonthlySelected={setMonthlySelected}
						/>
						<div className="mt-6 flex w-full gap-4 overflow-x-auto pb-2">
							{plans.data.map(plan => (
								<Plan
									key={plan.id}
									id={plan.id}
									title={plan.title}
									description={plan.description}
									usps={plan.usps}
									price={
										plan.price !== null
											? isMonthlySelected
												? { amount: plan.price.month, interval: "month" }
												: { amount: plan.price.year, interval: "year" }
											: null
									}
									onSelect={() => handleSelectPlan(plan)}
									submittingPlanId={submittingPlanId}
								/>
							))}
						</div>
						<p className="mt-4 flex items-center gap-2 text-center text-sm text-slate-500 sm:hidden">
							Scroll to explore more plans <ArrowIcon direction="right" className="size-5" />
						</p>
						<Button
							variant="link"
							onClick={nextStepUrl}
							className="hover:text-primary mt-6 cursor-pointer text-center font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 transition-colors"
						>
							I want to skip the free trial and continue with the free plan
						</Button>
					</motion.div>
				)}
			</main>
		</Layout>
	)
}

export default SelectPlanPage
